import { useCallback, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import type { SolutionMove } from '../store/gameStore';
import { useHaptic } from './useHaptic';
import * as api from '../api';
import type { HintResponse, SolutionNode } from '../types';

function buildSolutionPath(nodes: SolutionNode[]): SolutionMove[] {
  const path: SolutionMove[] = [];
  let current = nodes.find(n => n.correct) ?? nodes[0];
  let moveNum = 1;

  while (current) {
    path.push({ player: current.player, coord: current.coord, moveNumber: moveNum++ });
    const next = current.children?.find(c => c.correct) ?? current.children?.[0];
    if (!next) break;
    current = next;
  }

  return path;
}

export function useProblem() {
  const {
    currentProblem, sessionId, result, startTime, hintLevel, isLoading, solutionPath,
    setProblem, setResult, addMove, setSessionId, setHintLevel, setLoading, setSolutionPath,
  } = useGameStore();
  const haptic = useHaptic();
  const [hint, setHint] = useState<HintResponse | null>(null);

  const loadRandom = useCallback(async (difficulty?: number) => {
    setLoading(true);
    try {
      const problem = await api.getRandomProblem(difficulty);
      setProblem(problem);
      setHint(null);
    } finally {
      setLoading(false);
    }
  }, [setProblem, setLoading]);

  const loadProblem = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const problem = await api.getProblem(id);
      setProblem(problem);
      setHint(null);
    } finally {
      setLoading(false);
    }
  }, [setProblem, setLoading]);

  const submitMove = useCallback(async (coord: string) => {
    if (!currentProblem || result?.solved) return;

    addMove(coord);
    const timeMs = startTime ? Date.now() - startTime : 0;

    try {
      const moveResult = await api.solveProblem(
        currentProblem.id,
        coord,
        timeMs,
        sessionId || undefined,
      );

      setResult(moveResult);

      if (moveResult.session_id) {
        setSessionId(moveResult.session_id);
      }

      if (moveResult.correct) {
        haptic.success();
      } else {
        haptic.error();
      }

      return moveResult;
    } catch (err) {
      haptic.error();
      throw err;
    }
  }, [currentProblem, result, sessionId, startTime, addMove, setResult, setSessionId, haptic]);

  const requestHint = useCallback(async () => {
    if (!currentProblem) return;

    const newLevel = Math.min(hintLevel + 1, 2);
    setHintLevel(newLevel);

    try {
      const hintData = await api.getHint(currentProblem.id, newLevel);
      setHint(hintData);
      haptic.warning();
    } catch {
      // ignore
    }
  }, [currentProblem, hintLevel, setHintLevel, haptic]);

  const showSolution = useCallback(async () => {
    if (!currentProblem) return;
    try {
      const data = await api.getSolution(currentProblem.id);
      const path = buildSolutionPath(data.solutions);
      setSolutionPath(path);
    } catch {
      // ignore
    }
  }, [currentProblem, setSolutionPath]);

  return {
    problem: currentProblem,
    result,
    isLoading,
    hint,
    hintLevel,
    solutionPath,
    loadRandom,
    loadProblem,
    submitMove,
    requestHint,
    showSolution,
  };
}
